import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import usePageTracking from './hooks/usePageTracking';
import { useIntercom } from './hooks/useIntercom';
import { AuthProvider } from './contexts/AuthContext';
import { DataCacheProvider } from './contexts/DataCacheContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import DashboardLayout from './components/DashboardLayout';
import Home from './components/Home';
import About from './components/About';
import Contact from './components/Contact';
import Login from './components/Login';
import Signup from './components/Signup';
import Waitlist from './components/Waitlist';
import Setup from './components/Setup';
import OperationalDashboard from './components/OperationalDashboard';
import Assistant from './components/Assistant';
import Plans from './components/Plans';
import CallLogs from './components/CallLogs';
import Bookings from './components/Bookings';
import Staff from './components/Staff';
import Services from './components/Services';
import SmartDispatch from './components/SmartDispatch';
import FAQSources from './components/FAQSources';
import OAuthCallback from './components/OAuthCallback';
import OAuthDemo from './components/OAuthDemo';
import Privacy from './components/Privacy';
import TermsOfConditions from './components/TermsOfConditions';
import TrustAndSafety from './components/TrustAndSafety';
import MultiObjectiveConfig from './components/MultiObjectiveConfig';
import ObjectiveAnalytics from './components/ObjectiveAnalytics';
import ReferralManager from './components/ReferralManager';
import BillingRedirect from './components/BillingRedirect';
import LiveCallView from './components/LiveCallView';
import LiveLinkHandler from './components/LiveLinkHandler';
import ResetPassword from './components/ResetPassword';
import Skills from './components/Skills';
import Integrations from './components/Integrations';
import SubscriptionCanceled from './components/SubscriptionCanceled';
import SubscriptionComplete from './components/SubscriptionComplete';
import Settings from './components/Settings';
import Logs from './components/Logs';
import AdminCustomerList from './components/AdminCustomerList';
import AdminSettings from './components/AdminSettings';
import { ToastContainer } from './components/Toast';
import './index.css';

function TrackingWrapper() {
  usePageTracking();
  useIntercom();
  return null;
}

function App() {
  return (
    <AuthProvider>
      <DataCacheProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <div className="App">
            <TrackingWrapper />
            <ToastContainer />
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/waitlist" element={<Waitlist />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<TermsOfConditions />} />
            <Route path="/toc" element={<TermsOfConditions />} />
            <Route path="/trust-and-safety" element={<TrustAndSafety />} />
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            <Route path="/oauth/demo" element={<OAuthDemo />} />
            
            {/* Protected Routes */}
            <Route path="/setup" element={
              <ProtectedRoute>
                <Setup />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <OperationalDashboard />
              </ProtectedRoute>
            } />
            <Route path="/assistant" element={
              <ProtectedRoute>
                <Assistant />
              </ProtectedRoute>
            } />
            <Route path="/assistant/voice" element={
              <ProtectedRoute>
                <Assistant />
              </ProtectedRoute>
            } />
            <Route path="/plans" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Plans skipRedirect={true} />
                </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/call-logs" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CallLogs />
                </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/bookings" element={
              <ProtectedRoute>
                  <Bookings />
              </ProtectedRoute>
            } />
            <Route path="/staff" element={
              <ProtectedRoute>
                  <Staff />
              </ProtectedRoute>
            } />
            <Route path="/services" element={
              <ProtectedRoute>
                  <Services />
              </ProtectedRoute>
            } />
            <Route path="/smart-dispatch" element={
              <ProtectedRoute>
                  <SmartDispatch />
              </ProtectedRoute>
            } />
            <Route path="/faq-sources" element={
              <ProtectedRoute>
                  <FAQSources />
              </ProtectedRoute>
            } />
            <Route path="/objectives" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <MultiObjectiveConfig />
                </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/analytics" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ObjectiveAnalytics />
                </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/referrals" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ReferralManager />
                </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/billing" element={
              <ProtectedRoute>
                <BillingRedirect />
              </ProtectedRoute>
            } />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/skills" element={
              <ProtectedRoute>
                  <DashboardLayout>
                      <Skills />
                  </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/integrations" element={
              <ProtectedRoute>
                  <DashboardLayout>
                      <Integrations />
                  </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/canceled" element={
              <ProtectedRoute>
                <SubscriptionCanceled />
              </ProtectedRoute>
            } />
            <Route path="/subscription-complete" element={
              <ProtectedRoute skipRedirect={true}>
                <SubscriptionComplete />
              </ProtectedRoute>
            } />
            <Route path="/live/:sessionId" element={<LiveCallView />} />
            <Route path="/live-link/:linkId" element={<LiveLinkHandler />} />
            
            {/* Settings Routes */}
            <Route path="/settings" element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } />
            
            {/* Logs Routes */}
            <Route path="/logs" element={
              <ProtectedRoute>
                <Logs />
              </ProtectedRoute>
            } />
            <Route path="/logs/calls" element={
              <ProtectedRoute>
                <Logs />
              </ProtectedRoute>
            } />
            <Route path="/logs/sms" element={
              <ProtectedRoute>
                <Logs />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin/customers" element={
              <AdminProtectedRoute>
                <AdminCustomerList />
              </AdminProtectedRoute>
            } />
            <Route path="/admin/customer/:userId/settings" element={
              <AdminProtectedRoute>
                <AdminSettings />
              </AdminProtectedRoute>
            } />
            
            {/* Redirect any unknown routes to landing page */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
      </DataCacheProvider>
    </AuthProvider>
  );
}

export default App;